import { faThumbsDown, faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { UserContext } from '../../../context/UserProvider';
import { ChangeRequestClient } from '../../../services/ChangeRequestClient';
import { ChangeRequestReview } from '../../../types/ChangeRequestReview';
import { FuelTransaction } from '../../../types/FuelTransaction';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../ui/Form';
import { Textarea } from '../../ui/Textarea';
import {DashboardPageContext} from "../../../context/DashboardPageContext";

export const ReviewChangeRequestSchema = z.object({
    remark: z.string().optional(),
});

export const ReviewChangeRequest = ({ fuelTransaction }: { fuelTransaction: FuelTransaction }) => {
    const dashboardPageContext = useContext(DashboardPageContext);
    const queryClient = useQueryClient();
    const user = useContext(UserContext);
    const [isOpen, setIsOpen] = useState(false);
    const form = useForm<z.infer<typeof ReviewChangeRequestSchema>>({
        resolver: zodResolver(ReviewChangeRequestSchema),
        defaultValues: {
            remark: '',
        },
    });
    const reviewMutation = useMutation({
        mutationFn: (data: ChangeRequestReview) => ChangeRequestClient(user).reviewChangeRequest(data),
        onSuccess: () => {
            queryClient.invalidateQueries();
        },
    });

    const approve = async () => {
        reviewMutation.mutate(
            { id: fuelTransaction.changeRequest!.id, action: 'approve' },
            {
                onSuccess: () => {
                    toast('Succesvol goedgekeurd.');
                },
            },
        );
    };

    const reject = async ({ remark }: { remark?: string }) => {
        reviewMutation.mutate(
            { id: fuelTransaction.changeRequest!.id, action: 'reject', remark: remark },
            {
                onSuccess: () => {
                    setIsOpen(false);

                    toast('Succesvol afgekeurd.');
                },
            },
        );
    };

    return (
        <>
            <Button.Icon aria-label="approve" onClick={() => approve()} loading={reviewMutation.isPending} title="Goedkeuren">
                <FontAwesomeIcon icon={faThumbsUp} className="text-lg" />
            </Button.Icon>
            <Button.Icon aria-label="reject" onClick={() => setIsOpen(true)} title="Afkeuren">
                <FontAwesomeIcon icon={faThumbsDown} className="text-lg" />
            </Button.Icon>

            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <Dialog.Title>Tankbeurt afkeuren</Dialog.Title>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(reject)} className="space-y-3">
                        <FormField
                            control={form.control}
                            name="remark"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Opmerking (optioneel)</FormLabel>
                                    <FormControl>
                                        <Textarea placeholder="Opmerking (optioneel)" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="flex justify-end pt-6">
                            <Button loading={reviewMutation.isPending}>Afkeuren</Button>
                        </div>
                    </form>
                </Form>
            </Dialog>
        </>
    );
};
